import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'


import { AuthLayout } from './AuthLayout'
import { Field, FormError, SubmitButton } from './Field'

import { useSignIn } from '@/lib/auth/queries'

export function SignInPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const signIn = useSignIn()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  // Preserved through the sign-in redirect, so the user lands where they were going.
  const next = params.get('next') ?? '/'

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    // mutate() rather than mutateAsync(): the error lands in signIn.error and is
    // rendered, with no floating promise for the handler to drop.
    signIn.mutate(
      { email, password },
      { onSuccess: () => void navigate(next, { replace: true }) },
    )
  }

  return (
    <AuthLayout
      title="Welcome back."
      subtitle="Sign in to your workspace. Good things are waiting."
      footer={
        <>
          Don’t have an account? <Link to="/auth/signup">Create one</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {signIn.error && <FormError message={signIn.error.message} />}

        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          // A single-purpose form IS this page's content, so focusing the first
          // field is orienting rather than disruptive.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          disabled={signIn.isPending}
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          disabled={signIn.isPending}
        />

        <div style={{ textAlign: 'right', marginBottom: 'var(--spacing-4)' }}>
          <Link to="/auth/forgot" style={{ fontSize: 'var(--text-caption)' }}>
            Forgot your password?
          </Link>
        </div>

        <SubmitButton loading={signIn.isPending}>Sign in</SubmitButton>
      </form>
    </AuthLayout>
  )
}
